/**
 * Registry of all notification workflows in the system.
 * Each workflow maps to one or more edge functions.
 *
 * matchField: which field to match on when querying logs
 *   - For email: email_logs.template_name or email_logs.template_key
 *   - For push: notifications.category
 * matchValue: the value to match against
 */
export const WORKFLOW_REGISTRY = [
  {
    id: 'submission_confirmation',
    name: 'Submission Confirmation',
    edgeFunction: 'submission_confirmation_email',
    channel: 'email',
    trigger: 'auto',
    matchField: 'template_key',
    matchValue: 'entry_received',
    description: 'Sent when an artist submits an application',
  },
  {
    id: 'artist_inquiry',
    name: 'Artist Inquiry',
    edgeFunction: 'onesignal_push_artist_v2',
    channel: 'push',
    trigger: 'auto',
    matchField: 'category',
    matchValue: 'collector_inquiry',
    description: 'Push notification when a collector inquires about artwork',
  },
  {
    id: 'submission_approval',
    name: 'Submission Approval',
    edgeFunction: 'mandrill_submission_approval',
    channel: 'email',
    trigger: 'auto',
    matchField: 'template_name',
    matchValue: 'mandrill_submission_approval',
    description: 'Email when an artist submission is approved',
  },
  {
    id: 'org_email_campaigns',
    name: 'Org Email Campaigns',
    edgeFunction: 'organization_email_hub',
    channel: 'email',
    trigger: 'manual',
    matchField: 'template_name',
    matchValue: 'organization_email_hub',
    description: 'Bulk email campaigns sent by organization admins',
  },
  {
    id: 'welcome_email',
    name: 'Welcome Email',
    edgeFunction: 'mandrill_welcome_email',
    channel: 'email',
    trigger: 'auto',
    matchField: 'template_name',
    matchValue: 'mandrill_welcome_email',
    description: 'Welcome email for new users',
  },
  {
    id: 'event_reminder',
    name: 'Event Reminder',
    edgeFunction: 'send_event_reminder',
    channel: 'both',
    trigger: 'auto',
    matchField: 'template_name',
    matchValue: 'send_event_reminder',
    description: 'Reminders for upcoming events',
  },
  {
    id: 'comp_ticket',
    name: 'Comp Ticket Delivery',
    edgeFunction: 'send_comp_ticket_email',
    channel: 'email',
    trigger: 'manual',
    matchField: 'template_name',
    matchValue: 'send_comp_ticket_email',
    description: 'Complimentary ticket emails with QR codes',
  },
  {
    id: 'purchase_confirmation',
    name: 'Purchase Confirmation',
    edgeFunction: 'send_purchase_confirmation',
    channel: 'email',
    trigger: 'auto',
    matchField: 'template_name',
    matchValue: 'send_purchase_confirmation',
    description: 'Order confirmation after purchase',
  },
  {
    id: 'contact_email',
    name: 'Contact Email',
    edgeFunction: 'send-contact-email',
    channel: 'email',
    trigger: 'manual',
    matchField: 'template_name',
    matchValue: 'send-contact-email',
    description: 'Direct emails to organization contacts',
  },
  {
    id: 'commission_request',
    name: 'Commission Request',
    edgeFunction: 'mandrill_commission_request',
    channel: 'email',
    trigger: 'auto',
    matchField: 'template_name',
    matchValue: 'mandrill_commission_request',
    description: 'Commission request notifications',
  },
  {
    id: 'auction_bid',
    name: 'Auction Bid',
    edgeFunction: 'mandrill_auction_bid',
    channel: 'email',
    trigger: 'auto',
    matchField: 'template_name',
    matchValue: 'mandrill_auction_bid',
    description: 'Auction bid notifications',
  },
  {
    id: 'event_invite',
    name: 'Event Invite',
    edgeFunction: 'mandrill_event_invite',
    channel: 'email',
    trigger: 'manual',
    matchField: 'template_name',
    matchValue: 'mandrill_event_invite',
    description: 'Event invitation emails',
  },
  {
    id: 'collection_consideration',
    name: 'Collection Consideration',
    edgeFunction: 'mandrill_collection_consideration',
    channel: 'email',
    trigger: 'auto',
    matchField: 'template_name',
    matchValue: 'mandrill_collection_consideration',
    description: 'Artwork considered for collection notifications',
  },
];

/**
 * Health status thresholds
 */
export const HEALTH_THRESHOLDS = {
  DELIVERY_HEALTHY: 0.95,
  ENGAGEMENT_LOW: 0.20,
  STALE_DAYS: 7,
  INACTIVE_DAYS: 30,
};

/**
 * Compute health status for a workflow given its metrics.
 * @param {object} metrics - { deliveryRate, engagementRate, lastFiredAt, volume7d }
 * @returns {'healthy' | 'degraded' | 'stale' | 'inactive'}
 */
export function computeWorkflowHealth(metrics) {
  const { deliveryRate, engagementRate, lastFiredAt, volume7d } = metrics;

  if (volume7d === 0 && lastFiredAt) {
    const daysSinceLastFire = (Date.now() - new Date(lastFiredAt).getTime()) / (1000 * 60 * 60 * 24);
    if (daysSinceLastFire >= HEALTH_THRESHOLDS.INACTIVE_DAYS) return 'inactive';
    if (daysSinceLastFire >= HEALTH_THRESHOLDS.STALE_DAYS) return 'stale';
  }

  if (volume7d === 0 && !lastFiredAt) return 'inactive';

  if (deliveryRate != null && deliveryRate < HEALTH_THRESHOLDS.DELIVERY_HEALTHY) return 'degraded';
  if (engagementRate != null && engagementRate < HEALTH_THRESHOLDS.ENGAGEMENT_LOW) return 'degraded';

  return 'healthy';
}

/**
 * Status colors for health indicators
 */
export const HEALTH_COLORS = {
  healthy: '#4ade80',
  degraded: '#fbbf24',
  stale: '#fbbf24',
  inactive: '#6b7280',
};

/**
 * Status colors for delivery status indicators
 */
export const DELIVERY_STATUS_COLORS = {
  sent: '#a78bfa',
  delivered: '#4ade80',
  opened: '#fbbf24',
  clicked: '#f472b6',
  bounced: '#f87171',
  failed: '#f87171',
  spam: '#f87171',
  unsubscribed: '#6b7280',
  rejected: '#f87171',
  dismissed: '#6b7280',
  pending: '#6b7280',
};
